<template>
    <div class="base-applicable-multi-select"
        ref="dropdown">
        <div class="base-applicable-multi-select__header"
            :class="{ 'base-applicable-multi-select__header_open': isOpened }"
            @click.stop="toggleDropdown">
            <span class="base-applicable-multi-select__title">{{ displayTitle }}</span>
            <span class="base-applicable-multi-select__arrow"
                :class="{ 'base-applicable-multi-select__arrow_open': isOpened }"></span>
        </div>

        <template v-if="isOpened">
            <div class="base-applicable-multi-select__overlay"
                @click.stop="closeDropdown"></div>
            <div class="base-applicable-multi-select__dropdown"
                :style="dropdownStyle">
                <ul class="base-applicable-multi-select__list">
                    <li v-for="option in options"
                        class="base-applicable-multi-select__item"
                        :key="option.value"
                        @click.stop="toggleOption(option)">
                        <span class="base-applicable-multi-select__checkbox"
                            :class="{ 'base-applicable-multi-select__checkbox_checked': isSelected(option.value) }">
                        </span>
                        <span class="base-applicable-multi-select__item-label">{{ option.label }}</span>
                    </li>
                </ul>

                <div class="base-applicable-multi-select__actions">
                    <button v-if="areAllSelected"
                        class="base-applicable-multi-select__button base-applicable-multi-select__button_clear"
                        @click.stop="clearSelection">
                        Clear All
                    </button>
                    <button v-else
                        class="base-applicable-multi-select__button base-applicable-multi-select__button_clear"
                        @click.stop="selectAll">
                        Select All
                    </button>
                    <!-- <button class="base-applicable-multi-select__button base-applicable-multi-select__button_cancel"
                        @click.stop="cancelSelection">
                        Cancel
                    </button> -->
                    <button v-if="!autoApply"
                        class="base-applicable-multi-select__button base-applicable-multi-select__button_apply"
                        @click.stop="applySelection">
                        Apply
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import _ from 'lodash'

export default {
    name: 'BaseApplicableMultiSelect',
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        options: {
            type: Array,
            default: () => []
        },
        value: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            required: true
        },
        // do not overwrite title with selected items
        keepTitle: {
            type: Boolean,
            default: false
        },
        // hide apply button and apply selection automatically
        autoApply: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpened: false,
            dropdownStyle: {},
            tempSelectedValues: [],
        }
    },
    computed: {
        displayTitle() {
            if (this.keepTitle) {
                return this.title
            }
            if (this.tempSelectedValues.length === 0) {
                return this.title
            }
            if (this.tempSelectedValues.length === 1) {
                const selectedOption = this.options.find(opt => opt.value === this.tempSelectedValues[0])
                return selectedOption ? selectedOption.label : this.title
            }
            return `${this.title} (${this.tempSelectedValues.length} selected)`
        },
        areAllSelected() {
            return this.tempSelectedValues.length === this.options.length
        }
    },
    watch: {
        value: {
            handler(newVal) {
                this.tempSelectedValues = [...newVal]
            },
            immediate: true
        },
    },
    mounted() {
        this.debouncedResize = _.debounce(() => {
            this.setDropdownStyle()
        }, 50)
        window.addEventListener('resize', this.debouncedResize, false)
    },
    beforeDestroy() {
        if (this.debouncedResize) {
            window.removeEventListener('resize', this.debouncedResize)
        }
    },
    methods: {
        toggleDropdown() {
            if (this.isOpened) {
                this.closeDropdown()
            } else {
                this.openDropdown()
            }
        },
        openDropdown() {
            this.isOpened = true
            this.tempSelectedValues = [...this.value]
            this.setDropdownStyle()
        },
        closeDropdown() {
            this.isOpened = false
        },
        toggleOption(option) {
            const index = this.tempSelectedValues.indexOf(option.value)
            if (index > -1) {
                this.tempSelectedValues.splice(index, 1)
            } else {
                this.tempSelectedValues.push(option.value)
            }
            if (this.autoApply) {
                this.applySelection()
            }
        },
        isSelected(value) {
            return this.tempSelectedValues.includes(value)
        },
        clearSelection() {
            this.tempSelectedValues = []
            if (this.autoApply) {
                this.applySelection()
            }
        },
        selectAll() {
            this.tempSelectedValues = this.options.map(option => option.value)
            if (this.autoApply) {
                this.applySelection()
            }
        },
        cancelSelection() {
            this.tempSelectedValues = [...this.value] // Reset to original values
            this.closeDropdown()
        },
        applySelection() {
            this.$emit('input', [...this.tempSelectedValues])
            if (!this.autoApply) {
                this.closeDropdown()
            }
        },
        setDropdownStyle() {
            const ref = this.$refs.dropdown
            if (!ref) {
                return
            }

            const style = {}
            const { top, bottom, left, right } = ref.getBoundingClientRect()
            const topShift = top
            const bottomShift = window.innerHeight - bottom

            let verticalProp, maxHeight
            if (topShift < bottomShift) {
                verticalProp = 'top'
                maxHeight = Math.round(bottomShift) - 30
            } else {
                verticalProp = 'bottom'
                maxHeight = Math.round(top) - 30
            }

            style[verticalProp] = '100%'
            style['max-height'] = `${Math.min(maxHeight, 300)}px`

            if (verticalProp === 'top') {
                style['border-top'] = '2px solid #284AFF'
                style['border-radius'] = '0 0 8px 8px'
            } else {
                style['border-bottom'] = '2px solid #284AFF'
                style['border-radius'] = '8px 8px 0 0'
            }

            const leftShift = left
            const rightShift = window.innerWidth - right
            const horizontalProp = rightShift < leftShift ? 'right' : 'left'
            style[horizontalProp] = 0

            this.dropdownStyle = style
        }
    }
}
</script>

<style lang="scss" scoped>
.base-applicable-multi-select {
    position: relative;
    width: 200px;
    min-width: 180px;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        border-radius: 4px;
        border: 1px solid rgba(1, 22, 39, 0.1);
        padding: 8px 12px;
        cursor: pointer;
        height: 40px;
        box-sizing: border-box;
        background-color: #fff;

        &:hover {
            background-color: rgba(40, 74, 255, 0.1);
        }

        &_open {
            background-color: rgba(40, 74, 255, 0.1);
        }
    }

    &__title {
        color: #011627;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
    }

    &__arrow {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;

        &_open {
            transform: rotate(225deg);
        }
    }

    &__overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9998;
    }

    &__dropdown {
        position: absolute;
        z-index: 9999;
        min-width: 100%;
        max-width: 400px;
        background: #fff;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        border: 1px solid rgba(1, 22, 39, 0.1);
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0;
        max-height: 200px;
        overflow-y: auto;
    }

    &__item {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid rgba(1, 22, 39, 0.05);

        &:hover {
            background-color: rgba(40, 74, 255, 0.1);
        }

        &:last-child {
            border-bottom: none;
        }
    }

    &__checkbox {
        width: 16px;
        height: 16px;
        border: 2px solid #ccc;
        border-radius: 3px;
        margin-right: 8px;
        position: relative;
        flex-shrink: 0;

        &_checked {
            background-color: #284AFF;
            border-color: #284AFF;

            &::after {
                content: '';
                position: absolute;
                left: 5px;
                top: 1px;
                width: 4px;
                height: 8px;
                border: solid white;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
            }
        }
    }

    &__item-label {
        font-size: 14px;
        color: #011627;
        flex: 1;
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        padding: 12px;
        border-top: 1px solid rgba(1, 22, 39, 0.1);
        background-color: #f8f9fa;
    }

    &__button {
        padding: 6px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: #fff;
        color: #011627;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background-color: #f0f0f0;
        }

        &_clear {
            color: #666;
        }

        &_cancel {
            color: #666;
        }

        &_apply {
            background-color: #284AFF;
            color: white;
            border-color: #284AFF;

            &:hover {
                background-color: #1e3acc;
            }
        }
    }
}
</style>
